import React, { useState, useEffect } from 'react';
import DynamicEntityForm from './DynamicEntityForm';
import styles from './EntityTabsContainer.module.css';
import { fetchEntityConfig, fetchEntityData } from '../../services/workbenchDataService';

const EntityTabsContainer = ({ claimId, associatedEntityData, initialActiveTab = 'CLAIM_DETAILS' }) => {
  const [activeTab, setActiveTab] = useState(initialActiveTab);
  const [entityConfigs, setEntityConfigs] = useState({});
  const [entityData, setEntityData] = useState({});
  const [availableTabs, setAvailableTabs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const loadEntityData = async () => {
      try {
        setLoading(true);
        const tabs = [];
        const configs = {};
        const data = {};

        // If associated entity data was passed from navigation, fetch dynamically from API
        if (associatedEntityData && Object.keys(associatedEntityData).length > 0) {
          // Fetch entity configs and data from API for each entity
          const entityPromises = Object.entries(associatedEntityData).map(async ([entityCode, entityInfo]) => {
            try {
              const entityCd = entityInfo.entity_cd;
              const entityId = entityInfo.entity_id;
              
              // Fetch entity config from API
              const config = await fetchEntityConfig(entityCd);
              
              // Fetch entity data from API
              const entityDataResult = await fetchEntityData(entityCd, entityId);
              
              return {
                entityCode,
                config,
                data: entityDataResult,
                displayName: config?.displayName || config?.name || entityCode.replace(/_/g, ' '),
              };
            } catch (error) {
              console.error(`Failed to load entity ${entityCode}:`, error);
              return null;
            }
          });

          const results = await Promise.all(entityPromises);
          
          // Process results and build tabs
          results.forEach((result, index) => {
            if (result) {
              const { entityCode, config, data: entityDataResult, displayName } = result;
              
              configs[entityCode] = config;
              data[entityCode] = entityDataResult;
              
              tabs.push({
                key: entityCode,
                displayName,
                order: index
              });
            }
          });
        }

        // Sort tabs by order
        tabs.sort((a, b) => a.order - b.order);
        
        setAvailableTabs(tabs);
        setEntityConfigs(configs);
        setEntityData(data);
        
        // Set initial active tab if specified, otherwise use first available
        if (tabs.length > 0) {
          const tabExists = tabs.find(t => t.key === initialActiveTab);
          if (tabExists) {
            setActiveTab(initialActiveTab);
          } else if (!tabs.find(t => t.key === activeTab)) {
            setActiveTab(tabs[0].key);
          }
        }
      } catch (error) {
        console.error('Error loading entity data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (associatedEntityData && Object.keys(associatedEntityData).length > 0) {
      loadEntityData();
    }
  }, [associatedEntityData, initialActiveTab]);

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingText}>Loading claim details...</div>
      </div>
    );
  }

  if (availableTabs.length === 0) {
    return (
      <div className={styles.emptyContainer}>
        <div className={styles.emptyText}>No claim details available</div>
      </div>
    );
  }

  return (
    <div className={styles.tabsContainer}>
      {/* Tab Navigation */}
      <div className={styles.tabNav}>
        {availableTabs.map(tab => (
          <button
            key={tab.key}
            className={`${styles.tabButton} ${activeTab === tab.key ? styles.tabButtonActive : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.displayName}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className={styles.tabContent}>
        {entityConfigs[activeTab] && entityData[activeTab] ? (
          <DynamicEntityForm
            config={entityConfigs[activeTab]}
            data={entityData[activeTab]}
            entityKey={activeTab}
            configBasePath={activeTab}
          />
        ) : (
          <div className={styles.noContent}>No data available for this tab</div>
        )}
      </div>
    </div>
  );
};

export default EntityTabsContainer;
